import React, { useEffect, useState } from "react";
import { Card, Row, Col, Statistic, Spin } from "antd";
import ExcelExportButton from "./ExcelExportButton";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import {
    ShoppingCartOutlined,
    DollarCircleOutlined,
    CreditCardOutlined,
    UndoOutlined
} from "@ant-design/icons";
import PageNavigation from "../Layout/PageNavigation";

const cardDesigns = [
    {
        key: 'total_sales',
        icon: <ShoppingCartOutlined />,
        gradient: "linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)",
        color: "#0284c7"
    },
    {
        key: 'total_purchases',
        icon: <DollarCircleOutlined />,
        gradient: "linear-gradient(135deg, #16a34a 0%, #bbf7d0 100%)",
        color: "#059669"
    },
    {
        key: 'total_expenses',
        icon: <CreditCardOutlined />,
        gradient: "linear-gradient(135deg, #f43f5e 0%, #fbcfe8 100%)",
        color: "#be185d"
    },
    {
        key: 'total_returns',
        icon: <UndoOutlined />,
        gradient: "linear-gradient(135deg, #fbbf24 0%, #facc15 100%)",
        color: "#ca8a04"
    }
];

const DashboardSummary: React.FC = () => {
    const { fetchDashboard } = useReportApi();
    const { t } = useTranslation();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboard().then(res => {
            setData(res);
            setLoading(false);
        });
    }, [fetchDashboard]);

    if (loading) return <Spin size="large" style={{ display: "block", margin: "70px auto" }} />;

    return (
        <div style={{ padding: "10px 0 30px 0" }}>
            <PageNavigation title={t('dashboard_report') as string} />
            <Row justify="end" style={{ marginBottom: 16 }}>
                <ExcelExportButton
                    fileName="dashboard-summary"
                    sheetName={t('dashboard_report') as string}
                    columns={[
                        { header: t('summary') as string, key: 'label', width: 26 },
                        { header: t('totalAmount') as string, key: 'value', width: 18 },
                    ]}
                    rows={cardDesigns.map(card => ({
                        label: t(card.key),
                        value: data ? Number(data[card.key]) || 0 : 0,
                    }))}
                    title={[t('dashboard_report') as string]}
                />
            </Row>
            <Row gutter={[22, 24]} justify="start" align="top">
                {cardDesigns.map((card, idx) => (
                    <Col xs={24} sm={12} md={12} lg={6} key={card.key}>
                        <Card
                            hoverable
                            bordered={false}
                            style={{
                                background: card.gradient,
                                color: "#fff",
                                borderRadius: 18,
                                minHeight: 144,
                                boxShadow: "0 6px 36px 0 rgba(39,21,107,0.11)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                position: "relative",
                                transition: "transform 0.16s, box-shadow 0.18s",
                                overflow: "hidden"
                            }}
                            bodyStyle={{
                                width: "100%",
                                padding: "34px 18px 26px 18px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center"
                            }}
                            className="dashboard-stat-card"
                        >
                            <div style={{
                                fontSize: 38,
                                marginBottom: 8,
                                color: "#fff",
                                textShadow: "0 2px 12px rgba(0,0,0,0.13)"
                            }}>
                                {card.icon}
                            </div>
                            <div style={{
                                fontWeight: 700,
                                fontSize: 20,
                                marginBottom: 6,
                                letterSpacing: 0.5,
                                color: "#fff"
                            }}>
                                {t(card.key)}
                            </div>
                            <Statistic
                                valueStyle={{
                                    fontWeight: 900,
                                    fontSize: 34,
                                    color: "#fff",
                                    textShadow: "0 1.5px 8px rgba(0,0,0,0.08)"
                                }}
                                value={data ? data[card.key] : 0}
                                precision={2}
                                groupSeparator=","
                                suffix=""
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
            {/* Card hover effect */}
            <style>
                {`
                .dashboard-stat-card {
                  transition: transform 0.17s cubic-bezier(.4,2.3,.3,1), box-shadow 0.18s;
                }
                .dashboard-stat-card:hover {
                  transform: translateY(-7px) scale(1.045);
                  box-shadow: 0 18px 48px 0 rgba(34,197,94,0.18), 0 2px 9px #a5b4fc;
                  z-index: 10;
                }
                `}
            </style>
        </div>
    );
};

export default DashboardSummary;
